<script>
  import { select, selectAll } from "d3-selection";
  import { hierarchy, cluster } from "d3-hierarchy";
  import { scaleLinear, scaleSqrt } from "d3-scale";
  import { descending } from "d3-array";
  import { beforeUpdate, afterUpdate, tick } from "svelte";
  import { quintOut } from "svelte/easing";
  import { fade, draw } from "svelte/transition";
  import tippy from "sveltejs-tippy";
  import TripNode from "./TripNode.svelte";

  export let allData;
  export let selectedYear;
  export let selectedIndicator;
  export let colorBy;
  export let observed;
  export let isMobile;

  const padding = { top: 20, right: 20, bottom: 20, left: 20 };

  $: width = 300;
  $: height = 280;

  $: w = 300;
  $: svgWidth = 400;

  $: yScale = scaleLinear()
    .domain([160, 0])
    .range([height - padding.bottom, padding.top]);

  $: bubbleScale = scaleSqrt()
    .domain([
      1,
      Math.max(
        ...allData.tripsData.map((d) =>
          Math.max(...d.children.map((c) => c.total))
        )
      ),
    ])
    .range([5, 18]);

  let yearData;
  let rootArray = [];

  beforeUpdate(() => {
    if (observed || isMobile) {
      drawChart(selectedYear, selectedIndicator);
    }
  });

  afterUpdate(() => {
    updateScreen();
  });

  async function updateScreen() {
    await tick();
    updateLateralNodes();
  }

  const drawChart = (year, indicator) => {
    yearData = allData.tripsData.map((d) => {
      return {
        name: "root",
        children: [
          ...d["children"]
            .filter((c) => c.year === year)
            .sort((a, b) => {
              console.log(a, a[indicator])
              let indicatorSort = a[indicator]
                .toString()
                .localeCompare(b[indicator].toString());
              let dateSort =
                new Date(a.date_of_announcement) -
                new Date(b.date_of_announcement);

              return indicatorSort || dateSort;
            }),
        ],
      };
    });

    rootArray = yearData.map((d) => {
      return d3Cluster(hierarchy(d));
    });
  };

  const formatTooltip = (data, selector) => {
    const template = `
      <p><span>Countr${data.data.total > 1 ? "ies" : "y"}:</span> ${
      data.data.country.length > 0 ? data.data.country : "N/A (Virtual)"
    }</p>
      <p><span>Itinerary:</span> ${data.data.description_english}</p>
    `;

    return {
      content: template,
      allowHTML: true,
      placement: "top",
      triggerTarget: document.getElementById(selector),
    };
  };

  $: svgSize = svgWidth > 440 ? 110 : parseInt(svgWidth / 4);

  // First arg of size is the total angle of the dendro
  // Second arg is the overall scale
  $: d3Cluster = cluster().size([180, svgSize]);

  // Returns x, y values that format the dendrogram radially
  function project(x, y) {
    // Value subtracted by x is the rotation of the dendro
    const angle = ((x - 180) / 180) * Math.PI;
    const radius = y;
    return [radius * Math.cos(angle), yScale(radius * Math.sin(angle))];
  }

  const drawPath = (d, isActive, reverse) => {
    const dist = isActive === "isActive" ? 55 : 30;

    if (reverse) {
      return (
        "M" +
        project(d.parent.x, d.parent.y) +
        "C" +
        project(d.parent.x, (d.y + d.parent.y) / 2) +
        " " +
        project(d.x, (d.y + d.parent.y + 50) / 2) +
        " " +
        project(d.x, d.y + dist)
      );
    } else {
      return (
        "M" +
        project(d.x, d.y + dist) +
        "C" +
        project(d.x, (d.y + d.parent.y + 50) / 2) +
        " " +
        project(d.parent.x, (d.y + d.parent.y) / 2) +
        " " +
        project(d.parent.x, d.parent.y)
      );
    }
  };

  // Focuses (extend) trip path and circle on hover
  const focusPath = (id, bool) => {
    select(`#extend-${id}`).classed("isHidden", !bool);
    select(`#extend-circle-${id}`).classed("isHidden", !bool);
    select(`#path-${id}`).classed("isHidden", bool);
    select(`#circle-${id}`).classed("isHidden", bool);
    select(`#cover-${id}`).classed("isHidden", bool);

    updateLateralNodes();
  };

  const setNodePositions = (path, node, dist, rect) => {
    let x = path.getPointAtLength(dist).x;
    let y = path.getPointAtLength(dist).y;

    if (rect) {
      let rotateL = (Math.atan2(x, Math.abs(y)) * 180) / Math.PI;

      if (rotateL <= -85) {
        rotateL = -100;
      } else if (rotateL >= 85) {
        rotateL = 100;
      } else if (rotateL > 80 && rotateL < 85) {
        rotateL = 90;
      } else if (rotateL < -80 && rotateL > -85) {
        rotateL = -90;
      } else if (rotateL > 75 && rotateL < 80) {
        rotateL = 85;
      } else if (rotateL < -75 && rotateL > -80) {
        rotateL = -85;
      } else if (rotateL > 70 && rotateL < 75) {
        rotateL = 80;
      } else if (rotateL < -70 && rotateL > -75) {
        rotateL = -80;
      }

      node.setAttribute(
        "transform",
        `translate(${x - 2.5}, ${y - 2.5}) rotate(${rotateL}, 2.5, 2.5)`
      );
    } else {
      node.setAttribute("transform", `translate(${x}, ${y})`);
    }
  };

  const updateLateralNodes = () => {
    rootArray.forEach((rootObj, leaderIndex) => {
      if (!rootObj.data.children[0]) return;

      let allPaths = selectAll(
        `[data-id="${rootObj.data.children[0].top_leaders.replace(
          " ",
          "-"
        )}"] .trip-path`
      );
      allPaths.data(rootObj.children).each((a, tripIndex) => {
        let path = document.getElementById(
          `tooltip-path-${leaderIndex}-${tripIndex}`
        );

        // Update position of lateral nodes of currently hovered trip
        let tripPath = document.getElementById(
          `path-${leaderIndex}-${tripIndex}`
        );
        if (tripPath.classList.contains("isHidden")) {
          path = document.getElementById(`extend-${leaderIndex}-${tripIndex}`);
        }

        let biNode = document.getElementById(
          `biNode-${leaderIndex}-${tripIndex}`
        );

        let multiNode = document.getElementById(
          `multiNode-${leaderIndex}-${tripIndex}`
        );

        let tippyRef = document.getElementById(
          `tippyRef-${leaderIndex}-${tripIndex}`
        );

        setNodePositions(path, biNode, 30, false);
        setNodePositions(path, multiNode, 40, true);
        setNodePositions(path, tippyRef, 60, false);
      });
    });
  };

  const leaderInfo = {
    "Xi Jinping": {
      title: "President",
      delay: 800,
    },
    "Li Keqiang": {
      title: "Premier",
      delay: 1600,
    },
    "Yang Jiechi": {
      title: "Director, Central Foreign Affairs Commission Office",
      delay: 2400,
    },
    "Wang Yi": {
      title: "Foreign Minister",
      delay: 3200,
    },
    Others: {
      title: "",
      delay: 4000,
    },
  };
</script>

{#key rootArray}
  <div class="interactive__charts charts__container" bind:clientWidth={w} style="min-height:{isMobile ? height * 6 : height * 2 }px; width: 100%;">
    {#each rootArray as leader, leaderIndex}
      <div
        class="interactive__charts-wrapper"
        data-id={leader.children
          ? leader.children[0].data.top_leaders.replace(" ", "-")
          : ""}
      >
        <figure
          bind:clientWidth={svgWidth}
          height={height}
          class="leader leader-{leader.children
            ? leader.children[0].data.top_leaders.replace(' ', '-')
            : ''}"
          in:fade={{ delay: 200 }}
        >
          <svg {width} {height}>
            <defs>
              {#each leader.descendants().slice(1) as trip, i}
                <marker
                  id="marker-{leaderIndex}-{i}"
                  refX={bubbleScale(trip.data.total)}
                  refY={bubbleScale(trip.data.total)}
                  markerWidth={bubbleScale(trip.data.total) * 2}
                  markerHeight={bubbleScale(trip.data.total) * 2}
                  orient="auto"
                >
                  <circle
                    class="node-marker"
                    cx={bubbleScale(trip.data.total)}
                    cy={bubbleScale(trip.data.total)}
                    r={bubbleScale(trip.data.total)}
                    fill="#fff"
                  />
                </marker>
              {/each}
            </defs>
            <g class="g1" transform="translate({svgWidth / 2}, 235)">
              {#each leader.descendants().slice(1) as trip, tripIndex}
                <path
                  id={`extend-${leaderIndex}-${tripIndex}`}
                  class="extend-path isHidden"
                  d={drawPath(trip, "isActive", false)}
                  fill="none"
                  stroke="#303f46"
                  data-virtual={trip.data.region}
                  marker-start="url(#marker-{leaderIndex}-{tripIndex})"
                />

                <path
                  id={`path-${leaderIndex}-${tripIndex}`}
                  class="trip-path"
                  d={drawPath(trip, false, true)}
                  fill="none"
                  stroke="#303f46"
                  data-virtual={trip.data.region}
                  marker-end="url(#marker-{leaderIndex}-{tripIndex})"
                  in:draw={isMobile === false
                    ? {
                        duration: 1000,
                        delay:
                          leaderInfo[leader.children[0].data.top_leaders].delay,
                        easing: quintOut,
                      }
                    : {
                        duration: 0,
                        delay: 0,
                      }}
                />

                <path
                  id={`tooltip-path-${leaderIndex}-${tripIndex}`}
                  class="tooltip-path"
                  d={drawPath(trip, false, false)}
                  fill="none"
                  stroke="transparent"
                  stroke-width={8 +
                    (leader.children.length > 20
                      ? 0
                      : 20 - leader.children.length)}
                  on:mouseenter={() =>
                    focusPath(`${leaderIndex}-${tripIndex}`, true)}
                  on:mouseleave={() =>
                    focusPath(`${leaderIndex}-${tripIndex}`, false)}
                />

                <defs>
                  <pattern id="striped-bubble" width="10%" height="10%">
                    <circle id="striped-bubble" r="2" fill="#000" />
                  </pattern>
                </defs>

                <TripNode
                  uniqueIndex={`${leaderIndex}-${tripIndex}`}
                  name="circle"
                  r={bubbleScale(trip.data.total)}
                  translateVal={project(trip.x, trip.y + 30)}
                  {trip}
                  {colorBy}
                  leader={leader.children[0].data.top_leaders}
                  {isMobile}
                />

                <TripNode
                  uniqueIndex={`${leaderIndex}-${tripIndex}`}
                  name="extend-circle"
                  className="isHidden"
                  r={bubbleScale(trip.data.total)}
                  translateVal={project(trip.x, trip.y + 55)}
                  {trip}
                  {colorBy}
                  leader={leader.children[0].data.top_leaders}
                  {isMobile}
                />

                <circle
                  id={`extendTippyRef-${leaderIndex}-${tripIndex}`}
                  class="tippy-ref"
                  cx="0"
                  cy="0"
                  r={bubbleScale(trip.data.total)}
                  fill="transparent"
                  transform="translate({project(trip.x, trip.y + 55)})"
                  use:tippy={formatTooltip(
                    trip,
                    `tooltip-node-${leaderIndex}-${tripIndex}`
                  )}
                />

                <circle
                  id={`biNode-${leaderIndex}-${tripIndex}`}
                  class="bilateral-node"
                  class:isHidden={!trip.data.bilateral}
                  cx="0"
                  cy="0"
                  r="3"
                  fill="#303f46"
                  in:fade={isMobile === false
                    ? {
                        duration: 1500,
                        delay:
                          leaderInfo[leader.children[0].data.top_leaders]
                            .delay + 200,
                        easing: quintOut,
                      }
                    : { duration: 0, delay: 0 }}
                />

                <rect
                  id={`multiNode-${leaderIndex}-${tripIndex}`}
                  class="multilateral-node"
                  height="5"
                  width="5"
                  fill="#303f46"
                  in:fade={isMobile === false
                    ? {
                        duration: 1500,
                        delay:
                          leaderInfo[leader.children[0].data.top_leaders]
                            .delay + 200,
                        easing: quintOut,
                      }
                    : { duration: 0, delay: 0 }}
                />

                <TripNode
                  uniqueIndex={`${leaderIndex}-${tripIndex}`}
                  className="tooltip-node"
                  name="tooltip-node"
                  r={bubbleScale(trip.data.total) + 5}
                  translateVal={project(trip.x, trip.y + 30)}
                  {trip}
                  {focusPath}
                  leader={leader.children[0].data.top_leaders}
                  {isMobile}
                />

                <circle
                  id={`tippyRef-${leaderIndex}-${tripIndex}`}
                  class="tippy-ref"
                  cx="0"
                  cy="0"
                  r="5"
                  fill="transparent"
                  use:tippy={formatTooltip(
                    trip,
                    `tooltip-path-${leaderIndex}-${tripIndex}`
                  )}
                />
              {/each}
              <text
                in:fade={isMobile === false
                  ? {
                      duration: 1500,
                      delay:
                        leaderInfo[leader.children[0].data.top_leaders].delay,
                      easing: quintOut,
                    }
                  : { duration: 0, delay: 0 }}
                x="0"
                y="40"
                >{leader.children
                  ? leader.children[0].data.top_leaders
                  : ""}</text
              >
            </g>
          </svg>
        </figure>
        <label
          class="leader__titles"
          data-id={leader.children
            ? leader.children[0].data.top_leaders.replace(" ", "-")
            : ""}
          in:fade={isMobile === false
            ? {
                duration: 1500,
                delay: leaderInfo[leader.children[0].data.top_leaders].delay,
                easing: quintOut,
              }
            : { duration: 0, delay: 0 }}
        >
          {leaderInfo[leader.children[0].data.top_leaders].title}
        </label>
      </div>
    {/each}

    <div
      class="legend"
      style="max-height: {height}px; max-width: {svgWidth > 400
        ? 400
        : svgWidth}px;"
    >
      <div
        class="img-wrapper"
        in:fade={isMobile === false
          ? { duration: 1500, delay: 4800, easing: quintOut }
          : { duration: 0, delay: 0 }}
      >
        <img
          src={selectedIndicator === "region"
            ? "./images/Legend400x2 (2).svg"
            : "./images/Legend400x2Income (2).svg"}
          alt="Legend"
          title="Legend"
        />
      </div>
    </div>
  </div>
{/key}

<style type="text/scss" global>
  @import "../scss/custom/_chart.scss";
  @import "../scss/layout/_layout.scss";
  @import "../scss/components/_tooltips.scss";
</style>
